import { useParams } from "react-router-dom";
import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { VersionNavigation } from "@/components/VersionNavigation";
import { toast } from "@/hooks/use-toast";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";

interface TextWithNewlines {
    id: number;
    parent_id: number;
    original_text: string;
    translated_text: string;
    original_name: string;
    original_language: string;
    translated_language: string;
    status: string | null;
    is_accepted: boolean;
    created_at: string;
    updated_at: string;
}

const getCsrfToken = () => {
    const csrfTokenTag = document.querySelector("meta[name='csrf-token']");
    const csrfParamTag = document.querySelector("meta[name='csrf-param']");

    if (!csrfTokenTag || !csrfParamTag) {
        return { token: null, param: null };
    }

    return {
        token: csrfTokenTag.getAttribute("content"),
        param: csrfParamTag.getAttribute("content"),
    };
};

const Gomb12Page = () => {
    const { gameId, versionId } = useParams();
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editingContent, setEditingContent] = useState<string>("");
    const [saveStatus, setSaveStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
    const [currentPage, setCurrentPage] = useState(1);

    const { data, isLoading, isError, error, refetch } = useQuery({
        queryKey: ["textsWithNewlines", versionId, currentPage],
        queryFn: async () => {
            if (!versionId) return { texts: [], count: 0, page: 1, total_pages: 1 };

            const response = await fetch(
                `/api/extracted_texts/texts_with_newlines?version_id=${versionId}&page=${currentPage}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (!response.ok) {
                throw new Error("Failed to fetch texts with newlines");
            }

            return response.json();
        },
        enabled: !!versionId,
    });

    const updateTranslationMutation = useMutation({
        mutationFn: async ({ translationId, content }: { translationId: number; content: string }) => {
            const { token } = getCsrfToken();
            if (!token) {
                throw new Error("CSRF token nem található.");
            }

            const response = await fetch(`/api/extracted_texts/${translationId}`, {
                method: "PATCH",
                headers: {
                    "X-CSRF-Token": token,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    extracted_text: {
                        content: content,
                    }
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Fordítás frissítése sikertelen");
            }

            return response.json();
        },
        onSuccess: () => {
            setSaveStatus("success");
            setEditingId(null);
            setEditingContent("");
            toast({
                title: "Sikeres!",
                description: "A fordítás sikeresen mentve.",
            });
            setTimeout(() => setSaveStatus("idle"), 3000);
            refetch();
        },
        onError: (error) => {
            setSaveStatus("error");
            toast({
                title: "Hiba",
                description: error.message || "Ismeret hiba a mentés során",
                variant: "destructive",
            });
            setTimeout(() => setSaveStatus("idle"), 3000);
        }
    });

    const textsWithNewlines = data?.texts || [];

    const handleSave = () => {
        if (editingId !== null) {
            setSaveStatus("loading");
            updateTranslationMutation.mutate({
                translationId: editingId,
                content: editingContent,
            });
        }
    };

    const page = data?.page || 1;
    const totalPages = data?.total_pages || 1;

    return (
        <div className="w-full mx-auto py-6 px-4">
            <Breadcrumb className="mb-6">
                <BreadcrumbList>
                    <BreadcrumbItem>
                        <BreadcrumbLink href="/games">Játékok</BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                        <BreadcrumbLink href={`/games/${gameId}`}>{gameId}</BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                        <BreadcrumbLink href={`/games/${gameId}/versions/${versionId}`}>{versionId}</BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                        <span>Sortörések</span>
                    </BreadcrumbItem>
                </BreadcrumbList>
            </Breadcrumb>

            <div className="mb-6">
                <VersionNavigation gameId={gameId} versionId={versionId} />
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>Sortöréseket tartalmazó szövegek ({textsWithNewlines.length})</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    {isLoading ? (
                        <div className="text-center py-8 text-muted-foreground">
                            <p>Betöltés...</p>
                        </div>
                    ) : isError ? (
                        <div className="text-center py-8 text-red-600">
                            <p>Hiba: {error?.message || "Ismeretlen hiba"}</p>
                        </div>
                    ) : textsWithNewlines.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                            <p>Nincs olyan szöveg, amely sortöréseket tartalmaz.</p>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {textsWithNewlines.map((item: TextWithNewlines) => (
                                <div
                                    key={item.id}
                                    className={`border-2 rounded-lg p-4 space-y-4 ${item.is_accepted
                                            ? 'border-green-500 bg-green-50 dark:bg-green-950'
                                            : 'border-orange-500 bg-orange-50 dark:bg-orange-950'
                                        }`}
                                >
                                    {/* Status Badge */}
                                    <div className="flex justify-between items-center">
                                        <div>
                                            <span className={`text-xs font-bold px-3 py-1 rounded-full ${item.is_accepted
                                                    ? 'bg-green-600 text-white'
                                                    : 'bg-orange-600 text-white'
                                                }`}>
                                                {item.is_accepted ? '✓ Elfogadva' : '⏳ Függőben'}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        {/* Eredeti szöveg - read-only */}
                                        <div>
                                            <label className="text-sm font-semibold block mb-2">
                                                Eredeti szöveg ({item.original_name}):
                                            </label>
                                            <Textarea
                                                value={item.original_text}
                                                readOnly
                                                className="min-h-32 bg-muted"
                                            />
                                        </div>

                                        {/* Magyar fordítás - szerkeszthető */}
                                        <div>
                                            <label className="text-sm font-semibold block mb-2">
                                                Magyar fordítás (szerkeszthető):
                                            </label>
                                            {editingId === item.id ? (
                                                <div className="space-y-2">
                                                    <Textarea
                                                        value={editingContent}
                                                        onChange={(e) => setEditingContent(e.target.value)}
                                                        className="min-h-32"
                                                    />
                                                    <div className="flex gap-2">
                                                        <Button
                                                            onClick={handleSave}
                                                            disabled={saveStatus === "loading"}
                                                            className="bg-green-600 hover:bg-green-700"
                                                        >
                                                            {saveStatus === "loading" ? "Mentés..." : "Mentés"}
                                                        </Button>
                                                        <Button
                                                            onClick={() => {
                                                                setEditingId(null);
                                                                setEditingContent("");
                                                            }}
                                                            variant="outline"
                                                            disabled={saveStatus === "loading"}
                                                        >
                                                            Mégse
                                                        </Button>
                                                        {saveStatus === "success" && (
                                                            <span className="text-green-600 font-semibold flex items-center">
                                                                ✓ Mentve!
                                                            </span>
                                                        )}
                                                        {saveStatus === "error" && (
                                                            <span className="text-red-600 font-semibold flex items-center">
                                                                ✗ Hiba!
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="space-y-2">
                                                    <Textarea
                                                        value={item.translated_text}
                                                        readOnly
                                                        className="min-h-32"
                                                    />
                                                    <Button
                                                        onClick={() => {
                                                            setEditingId(item.id);
                                                            setEditingContent(item.translated_text);
                                                            setSaveStatus("idle");
                                                        }}
                                                        variant="outline"
                                                    >
                                                        Szerkesztés
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="text-xs text-muted-foreground pt-2 border-t">
                                        <p>ID: {item.id} | Módosítva: {new Date(item.updated_at).toLocaleDateString('hu-HU')}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Pagination */}
                    {textsWithNewlines.length > 0 && (
                        <div className="flex gap-2 justify-center items-center mt-6 pt-4 border-t">
                            <Button
                                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                disabled={currentPage === 1 || isLoading}
                                size="sm"
                            >
                                ← Előző
                            </Button>

                            <span className="text-sm text-muted-foreground px-4">
                                Oldal {page} / {totalPages}
                            </span>

                            <Button
                                onClick={() => setCurrentPage(currentPage + 1)}
                                disabled={currentPage >= totalPages || isLoading}
                                size="sm"
                            >
                                Következő →
                            </Button>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default Gomb12Page;
